import type { TemplateProposalBody } from "@rosc/contracts";
import {
  GenerationProgress,
  useTimedStep,
} from "@rosc/ui/v32/generation-progress";
import { useBlocker } from "@tanstack/react-router";
import {
  AlignLeft,
  ChevronDown,
  ChevronLeft,
  Eye,
  FileText,
  GripVertical,
  List,
  Loader2,
  Plus,
  ShieldCheck,
  Sparkles,
  Star,
  Trash2,
} from "lucide-react";
import type { JSX } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { V3Button, V3IconButton } from "@/components/ui/v3/button";
import { V3Chip } from "@/components/ui/v3/chip";
import { V3ConfirmSheet } from "@/components/ui/v3/confirm-sheet";
import { V3Drawer } from "@/components/ui/v3/drawer";
import {
  V3_MENU_CONTENT,
  V3_MENU_ITEM,
  V3_MENU_LABEL,
} from "@/components/ui/v3/menu";
import { useTemplateProposal } from "@/hooks/templates/useTemplateProposal";
import { useUnloadWarning } from "@/hooks/useUnloadWarning";
import {
  emptySection,
  fromTemplate,
  move,
  sameDocument,
  type TemplateDocument,
  type TemplateLength,
  type TemplateSection,
  type TemplateStyle,
  templateProblem,
  toTemplateWrite,
} from "@/lib/template-document";
import {
  applyTemplateDraft,
  type TemplateExtraction,
} from "@/lib/template-extraction";
import { cn } from "@/lib/utils";
import { AskBar } from "@/pages/Visit/workspace/v3/AskBar";
import { TemplateFromNotesDialog } from "./TemplateFromNotesDialog";

const FIELD =
  "w-full resize-none border-0 bg-transparent p-0 font-v3 text-v3-text outline-hidden placeholder:text-v3-text-3 focus-visible:outline-hidden";

// Grows a textarea with its text (no inner scroll bar). Measuring sets the
// height to "auto" for a moment, which shrinks the page; the scroll area then
// clamped its position and jumped up while typing in a long template. The
// scroll position is kept across the measurement, and the caret is then
// brought into view.
const useAutoHeight = (value: string) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: measured per value.
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const scroller = scrollParent(element);
    const top = scroller?.scrollTop ?? 0;
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
    if (!scroller) return;
    if (scroller.scrollTop !== top) scroller.scrollTop = top;
    if (document.activeElement === element) keepCaretInView(element, scroller);
  }, [value]);
  return ref;
};

// The nearest ancestor that scrolls vertically.
const scrollParent = (element: HTMLElement): HTMLElement | null => {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
  }
  return null;
};

// Margin kept between the caret line and the edges of the scroll area.
const CARET_MARGIN = 24;

// Scrolls just enough that the caret's line is visible: the caret's offset
// in the text is measured on a hidden copy of the textarea.
export const keepCaretInView = (
  element: HTMLTextAreaElement,
  scroller: HTMLElement,
): void => {
  const caretTop =
    element.getBoundingClientRect().top + caretOffsetTop(element);
  const lineHeight =
    Number.parseFloat(getComputedStyle(element).lineHeight) || 20;
  const rect = scroller.getBoundingClientRect();
  // Anything covering the top of the scroll area: its scroll-padding.
  const covered =
    Number.parseFloat(getComputedStyle(scroller).scrollPaddingTop) || 0;
  const top = rect.top + covered + CARET_MARGIN;
  const bottom = rect.bottom - CARET_MARGIN;
  if (caretTop + lineHeight > bottom)
    scroller.scrollTop += caretTop + lineHeight - bottom;
  else if (caretTop < top) scroller.scrollTop -= top - caretTop;
};

const MIRRORED = [
  "boxSizing",
  "width",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "letterSpacing",
  "lineHeight",
  "textTransform",
  "wordSpacing",
  "tabSize",
] as const;

// The caret's top inside the textarea, in px.
const caretOffsetTop = (element: HTMLTextAreaElement): number => {
  const mirror = document.createElement("div");
  const style = getComputedStyle(element);
  for (const key of MIRRORED) mirror.style[key] = style[key];
  Object.assign(mirror.style, {
    position: "absolute",
    visibility: "hidden",
    top: "0",
    left: "-9999px",
    whiteSpace: "pre-wrap",
    overflowWrap: "break-word",
  });
  mirror.textContent = element.value.slice(
    0,
    element.selectionEnd ?? element.value.length,
  );
  const marker = document.createElement("span");
  marker.textContent = "\u200b";
  mirror.appendChild(marker);
  document.body.appendChild(mirror);
  const top = marker.offsetTop;
  mirror.remove();
  return top;
};

// Whether an element is in view in the scroll area (true until measured).
const useInView = (
  root: React.RefObject<HTMLElement | null>,
): { ref: React.RefObject<HTMLDivElement | null>; value: boolean } => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [value, setValue] = useState(true);
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setValue(!!entry?.isIntersecting),
      { root: root.current, threshold: 0 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [root]);
  return { ref, value };
};

const Grow = ({
  value,
  onChange,
  className,
  ...props
}: Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange"> & {
  value: string;
  onChange: (value: string) => void;
}): JSX.Element => {
  const ref = useAutoHeight(value);
  return (
    <textarea
      ref={ref}
      className={cn(FIELD, className)}
      rows={1}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      {...props}
    />
  );
};

/** One section: drag handle, heading, instructions, required/optional, remove. */
const SectionCard = ({
  section,
  index,
  count,
  dragging,
  onChange,
  onRemove,
  onMove,
  onDragStart,
  cardRef,
}: {
  section: TemplateSection;
  index: number;
  count: number;
  dragging: boolean;
  onChange: (section: TemplateSection) => void;
  onRemove: () => void;
  onMove: (to: number) => void;
  onDragStart: (event: React.PointerEvent) => void;
  cardRef: (element: HTMLLIElement | null) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const name =
    section.heading.trim() || t("templateEditorV32.section.untitled");
  return (
    <li
      ref={cardRef}
      className={cn(
        "relative rounded-[14px] bg-v3-panel py-3 pl-11 pr-3 shadow-v3-hairline-strong transition-shadow focus-within:shadow-[inset_0_0_0_1.5px_var(--v3-ink)] sm:py-4 sm:pr-4",
        dragging && "z-10 shadow-v3-pop",
      )}
      data-testid="template-section"
    >
      <button
        aria-describedby="template-section-move-hint"
        aria-label={t("templateEditorV32.section.drag", { name })}
        className="absolute left-1 top-2 flex size-10 cursor-grab touch-none items-center justify-center rounded-full text-v3-text-3 hover:bg-v3-surface-2 hover:text-v3-text-2 focus-visible:outline-2 focus-visible:outline-v3-brand active:cursor-grabbing sm:top-3"
        data-testid="template-section-grip"
        type="button"
        onKeyDown={(event) => {
          if (event.key === "ArrowUp" && index > 0) {
            event.preventDefault();
            onMove(index - 1);
          } else if (event.key === "ArrowDown" && index < count - 1) {
            event.preventDefault();
            onMove(index + 1);
          }
        }}
        onPointerDown={onDragStart}
      >
        <GripVertical aria-hidden className="size-4" />
      </button>
      <div className="flex items-start gap-2">
        <input
          aria-label={t("templateEditorV32.section.heading")}
          className={cn(FIELD, "min-h-10 flex-1 text-[16px] font-semibold")}
          data-testid="template-section-heading"
          maxLength={100}
          placeholder={t("templateEditorV32.section.headingPlaceholder")}
          value={section.heading}
          onChange={(event) =>
            onChange({ ...section, heading: event.target.value })
          }
        />
        <V3Chip
          aria-label={t(
            section.optional
              ? "templateEditorV32.section.makeRequired"
              : "templateEditorV32.section.makeOptional",
            { name },
          )}
          className={cn(
            "mt-1",
            section.optional && "bg-v3-surface-2 text-v3-text-2",
          )}
          data-testid="template-section-optional"
          size="sm"
          onClick={() => onChange({ ...section, optional: !section.optional })}
        >
          {section.optional
            ? t("templateEditorV32.section.optional")
            : t("templateEditorV32.section.required")}
        </V3Chip>
        <V3IconButton
          className="-mr-1 shrink-0"
          data-testid="template-section-remove"
          label={t("templateEditorV32.section.remove", { name })}
          onClick={onRemove}
        >
          <Trash2 />
        </V3IconButton>
      </div>
      <Grow
        aria-label={t("templateEditorV32.section.instructions", { name })}
        className="mt-1 text-[14.5px] leading-[21px] text-v3-text-2"
        data-testid="template-section-instructions"
        placeholder={t("templateEditorV32.section.instructionsPlaceholder")}
        value={section.instructions}
        onChange={(instructions) => onChange({ ...section, instructions })}
      />
    </li>
  );
};

const OptionMenu = <T extends string>({
  icon,
  label,
  value,
  options,
  onChange,
  testId,
}: {
  icon: React.ReactNode;
  label: string;
  value: T | "default";
  options: { value: T | "default"; label: string }[];
  onChange: (value: T | "default") => void;
  testId: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const current = options.find((option) => option.value === value);
  // "Style: Account default", "Style: Bullet points".
  const text = t("templateEditorV32.chipValue", {
    label,
    value: current?.label ?? "",
  });
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <V3Chip aria-label={text} data-testid={testId}>
          {icon}
          {text}
          <ChevronDown aria-hidden className="text-v3-text-3" />
        </V3Chip>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className={V3_MENU_CONTENT}>
        <DropdownMenuLabel className={V3_MENU_LABEL}>{label}</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={(next) => onChange(next as T | "default")}
        >
          {options.map((option) => (
            <DropdownMenuRadioItem
              key={option.value}
              className={cn(V3_MENU_ITEM, "pl-8")}
              value={option.value}
            >
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** How a note from this template is laid out (no AI call). */
const PreviewBody = ({ doc }: { doc: TemplateDocument }): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div className="px-5 pb-6" data-testid="template-preview">
      <p className="text-[13px] leading-5 text-v3-text-3">
        {t("templateEditorV32.preview.hint")}
      </p>
      <article className="mt-4 rounded-[14px] p-5 shadow-v3-hairline-strong">
        <h3 className="text-[19px] font-bold leading-6">
          {doc.name.trim() || t("templateEditorV32.newTitle")}
        </h3>
        {doc.sections.map((section) => (
          <section key={section.key} className="mt-4">
            <h4 className="text-[12px] font-semibold uppercase tracking-[0.04em] text-v3-text-2">
              {section.heading.trim() ||
                t("templateEditorV32.section.untitled")}
            </h4>
            <p className="mt-1 text-[14px] leading-5 text-v3-text-3">
              {section.instructions.trim() ||
                t("templateEditorV32.preview.fromVisit")}
              {section.optional && (
                <span className="mt-1 block text-[12.5px]">
                  {t("templateEditorV32.preview.optional")}
                </span>
              )}
            </p>
          </section>
        ))}
      </article>
    </div>
  );
};

type Proposal = NonNullable<ReturnType<typeof useTemplateProposal>["result"]>;

/** Applies an assistant proposal, keeping guidance it did not restate. */
const applyProposal = (
  doc: TemplateDocument,
  proposal: Proposal["proposal"],
  name: string,
): TemplateDocument => {
  const next = fromTemplate({
    name,
    headers: proposal.headers,
    optionalText: proposal.rules,
    isDefault: doc.isDefault,
  });
  const before = new Map(
    doc.sections.map((section) => [
      section.heading.trim().toLowerCase(),
      section,
    ]),
  );
  const restated = proposal.rules.includes("[Template settings]");
  return {
    ...next,
    style: restated ? next.style : doc.style,
    length: restated ? next.length : doc.length,
    sections: next.sections.map((section) => {
      const old = before.get(section.heading.trim().toLowerCase());
      return restated || !old
        ? section
        : {
            ...section,
            instructions: old.instructions,
            optional: old.optional,
          };
    }),
  };
};

// While the assistant rewrites the template (one request, no stages of its
// own): reading, then (after 3 s) writing, current until the proposal
// arrives.
const TemplateRewriteProgress = (): JSX.Element => {
  const { t } = useTranslation();
  const step = useTimedStep([3000]);
  return (
    <GenerationProgress
      current={step}
      steps={[
        {
          key: "reading",
          label: t("generationProgress.steps.readingTemplate"),
        },
        {
          key: "writing",
          label: t("generationProgress.steps.writingChanges"),
        },
      ]}
      testId="template-rewriting"
      title={t("generationProgress.template.title")}
    />
  );
};

const ProposalCard = ({
  result,
  onApply,
  onDiscard,
}: {
  result: Proposal;
  onApply: () => void;
  onDiscard: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const { diff } = result;
  return (
    <section
      aria-label={t("templateEditorV32.proposal.title")}
      className="rounded-[14px] bg-v3-panel p-4 shadow-v3-pop"
      data-testid="template-proposal"
    >
      <p className="flex items-center gap-2 text-[14px] font-semibold">
        <Sparkles aria-hidden className="size-4 text-v3-text-2" />
        {t("templateEditorV32.proposal.title")}
      </p>
      {result.summaryOfChanges && (
        <p className="mt-1.5 text-[14px] leading-5 text-v3-text-2">
          {result.summaryOfChanges}
        </p>
      )}
      <ul className="mt-2 flex flex-wrap gap-1.5 text-[12.5px]">
        {diff.name.changed && (
          <li className="rounded-full bg-v3-surface-2 px-2.5 py-1">
            {t("templateEditorV32.proposal.renamed", { name: diff.name.after })}
          </li>
        )}
        {diff.headers.added.map((heading) => (
          <li
            key={`+${heading}`}
            className="rounded-full bg-v3-ok-tint px-2.5 py-1 text-v3-ok-text"
          >
            + {heading}
          </li>
        ))}
        {diff.headers.removed.map((heading) => (
          <li
            key={`-${heading}`}
            className="rounded-full bg-v3-rec-tint px-2.5 py-1 text-v3-rec-text line-through"
          >
            {heading}
          </li>
        ))}
        {diff.headers.reordered && (
          <li className="rounded-full bg-v3-surface-2 px-2.5 py-1">
            {t("templateEditorV32.proposal.reordered")}
          </li>
        )}
        {diff.rules.changed && (
          <li className="rounded-full bg-v3-surface-2 px-2.5 py-1">
            {t("templateEditorV32.proposal.rules")}
          </li>
        )}
      </ul>
      <div className="mt-3 flex justify-end gap-2">
        <V3Button data-testid="template-proposal-discard" onClick={onDiscard}>
          {t("templateEditorV32.proposal.discard")}
        </V3Button>
        <V3Button
          data-testid="template-proposal-apply"
          variant="primary"
          onClick={onApply}
        >
          {t("templateEditorV32.proposal.apply")}
        </V3Button>
      </div>
    </section>
  );
};

// New template: "Start from your notes" (upload or paste previous notes; the
// referral letter "from an example letter" flow for note templates).
const FromNotesCard = ({ onOpen }: { onOpen: () => void }): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div
      className="mt-6 flex flex-col gap-3 rounded-[14px] bg-v3-panel px-4 py-3.5 shadow-v3-hairline sm:flex-row sm:items-center"
      data-testid="template-from-notes-card"
    >
      <div className="flex min-w-0 flex-1 gap-3">
        <FileText
          aria-hidden
          className="mt-0.5 size-5 shrink-0 text-v3-text-2"
        />
        <div className="min-w-0">
          <p className="text-[14.5px] font-semibold leading-5 text-v3-text">
            {t("templateFromNotes.cardTitle")}
          </p>
          <p className="mt-0.5 text-[13px] leading-[18px] text-v3-text-2">
            {t("templateFromNotes.cardBody")}
          </p>
        </div>
      </div>
      <V3Button
        className="shrink-0 max-sm:h-11 max-sm:w-full"
        data-testid="template-from-notes-open"
        variant="secondary"
        onClick={onOpen}
      >
        <Sparkles aria-hidden />
        {t("templateFromNotes.open")}
      </V3Button>
    </div>
  );
};

// What the notes gave, and that no patient details were kept.
const FromNotesNotice = ({
  identifiersRemoved,
  summary,
  onAgain,
}: {
  identifiersRemoved: boolean;
  summary: string;
  onAgain: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div
      className="mt-6 flex gap-3 rounded-[14px] bg-v3-surface-2 px-4 py-3 text-[13.5px] leading-5 text-v3-text shadow-v3-hairline"
      data-testid="template-from-notes-notice"
      role="status"
    >
      <ShieldCheck
        aria-hidden
        className="mt-0.5 size-4 shrink-0 text-v3-ok-text"
      />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="font-semibold">{t("templateFromNotes.readyTitle")}</p>
        {summary && <p className="text-v3-text-2">{summary}</p>}
        <p className="text-v3-text-2">
          {identifiersRemoved
            ? t("templateFromNotes.identifiersRemoved")
            : t("templateFromNotes.noIdentifiers")}
        </p>
        <button
          className="-mx-1 inline-flex min-h-9 items-center rounded-md px-1 font-semibold text-v3-text underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring max-sm:min-h-11"
          data-testid="template-from-notes-again"
          type="button"
          onClick={onAgain}
        >
          {t("templateFromNotes.again")}
        </button>
      </div>
    </div>
  );
};

/**
 * The v3.2 template editor ("Document", VITE_APP_REDESIGN_ENABLED): the
 * template as a document of section cards (drag to reorder, heading and
 * instructions edited in place, required or optional), style, length and
 * default chips, a preview, Save and Discard with an unsaved-changes guard,
 * and "Ask ROSC to change this template" in the shared Ask bar (the
 * template assistant's proposal, applied only on Apply).
 */
export const TemplateDocumentEditor = ({
  template,
  templatePublicId,
  saving,
  onSave,
  onClose,
}: {
  template?: {
    name?: string | null;
    headers?: readonly string[] | null;
    optionalText?: string | null;
    isDefault?: boolean | number | null;
  };
  templatePublicId?: string;
  saving: boolean;
  /** Resolves true when saved (the page then leaves). */
  onSave: (write: ReturnType<typeof toTemplateWrite>) => Promise<boolean>;
  onClose: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const [initial, setInitial] = useState<TemplateDocument>(() =>
    template
      ? fromTemplate(template)
      : { ...fromTemplate({}), sections: [emptySection()] },
  );
  const [doc, setDoc] = useState<TemplateDocument>(initial);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [ask, setAsk] = useState("");
  const [instruction, setInstruction] = useState("");
  // The template as it was when the provider pressed Send. The proposal is
  // keyed on this snapshot, not the live document: typing a name (or any
  // other edit) while the proposal is pending or on screen must not discard
  // it.
  const [asked, setAsked] = useState<TemplateProposalBody["current"]>();
  const [request, setRequest] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesNotice, setNotesNotice] = useState<Omit<
    TemplateExtraction,
    "draft"
  > | null>(null);
  const cards = useRef(new Map<string, HTMLLIElement>());
  const scrollRef = useRef<HTMLDivElement>(null);
  const metaLine = useInView(scrollRef);
  const dirty = !sameDocument(doc, initial);

  const write = toTemplateWrite(doc);
  const proposal = useTemplateProposal({
    instruction: instruction || " ",
    templatePublicId,
    current: asked,
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: one proposal per Send.
  useEffect(() => {
    if (request > 0) void proposal.propose();
  }, [request]);

  // Unsaved edits: ask before leaving (in the app and when closing the tab).
  const blocker = useBlocker({
    shouldBlockFn: () => dirty && !leaving,
    withResolver: true,
    enableBeforeUnload: false,
  });
  useUnloadWarning(dirty && !leaving, t("templateEditorV32.unsaved.title"));

  const update = (next: Partial<TemplateDocument>): void => {
    setProblem(null);
    setDoc((current) => ({ ...current, ...next }));
  };
  const setSection = (index: number, section: TemplateSection): void =>
    update({
      sections: doc.sections.map((s, i) => (i === index ? section : s)),
    });

  const startDrag =
    (key: string) =>
    (event: React.PointerEvent): void => {
      if (event.button !== 0) return;
      const grip = event.currentTarget as HTMLElement;
      grip.setPointerCapture?.(event.pointerId);
      setDragKey(key);
      const onMoveEvent = (moveEvent: PointerEvent): void => {
        setDoc((current) => {
          const from = current.sections.findIndex((s) => s.key === key);
          let to = from;
          current.sections.forEach((section, index) => {
            const rect = cards.current
              .get(section.key)
              ?.getBoundingClientRect();
            if (!rect || index === from) return;
            const middle = rect.top + rect.height / 2;
            if (index < from && moveEvent.clientY < middle)
              to = Math.min(to, index);
            if (index > from && moveEvent.clientY > middle)
              to = Math.max(to, index);
          });
          return to === from
            ? current
            : { ...current, sections: move(current.sections, from, to) };
        });
      };
      const onUp = (): void => {
        setDragKey(null);
        grip.removeEventListener("pointermove", onMoveEvent);
        grip.removeEventListener("pointerup", onUp);
        grip.removeEventListener("pointercancel", onUp);
      };
      grip.addEventListener("pointermove", onMoveEvent);
      grip.addEventListener("pointerup", onUp);
      grip.addEventListener("pointercancel", onUp);
    };

  const save = async (): Promise<void> => {
    const reason = templateProblem(doc);
    if (reason) {
      setProblem(t(`templateEditorV32.problem.${reason}`));
      return;
    }
    setLeaving(true);
    const saved = await onSave(write);
    if (saved) setInitial(doc);
    else setLeaving(false);
  };

  const close = (): void => onClose();
  // Preview and Save: on the meta line, or in the top bar while the meta
  // line is out of view. Only one set is shown (and reachable) at a time.
  const actions = (place: "meta" | "top"): JSX.Element => {
    const shown = place === "meta" || !metaLine.value;
    return (
      <div
        aria-hidden={!shown || undefined}
        aria-label={t("templateEditorV32.actions")}
        className={cn(
          "flex shrink-0 items-center gap-0.5",
          place === "top" &&
            "transition-opacity duration-150 ease-out motion-reduce:transition-none",
          place === "top" && !shown && "pointer-events-none opacity-0",
        )}
        data-testid={place === "top" ? "template-top-actions" : undefined}
        inert={!shown || undefined}
        role="toolbar"
      >
        <V3Button
          aria-label={t("templateEditorV32.preview.open")}
          data-testid={
            place === "top"
              ? "template-preview-button-top"
              : "template-preview-button"
          }
          tabIndex={shown ? undefined : -1}
          variant="ghost"
          onClick={() => setPreviewOpen(true)}
        >
          <Eye />
          <span className="max-sm:hidden">
            {t("templateEditorV32.preview.open")}
          </span>
        </V3Button>
        <V3Button
          className="ml-1"
          data-testid={place === "top" ? "template-save-top" : "template-save"}
          disabled={saving || (!dirty && !!templatePublicId)}
          tabIndex={shown ? undefined : -1}
          variant="primary"
          onClick={() => {
            void save();
          }}
        >
          {saving && <Loader2 className="animate-spin" />}
          {t("templateEditorV32.save")}
        </V3Button>
      </div>
    );
  };
  const title = doc.name.trim() || t("templateEditorV32.newTitle");
  // "6 sections · Bullet points · Concise" (account defaults left out).
  const meta = [
    t("templateEditorV32.metaSections", { count: doc.sections.length }),
    doc.style && t(`templateEditorV32.style.${doc.style}`),
    doc.length && t(`templateEditorV32.length.${doc.length}`),
  ]
    .filter(Boolean)
    .join(" · ");
  const result = proposal.result;

  return (
    <div
      className="relative flex h-full min-h-0 flex-col bg-v3-panel font-v3 text-v3-text"
      data-testid="template-document-editor"
    >
      {/* Back + the template's name (truncated); Preview and Save fade in
          on the right once the meta line has scrolled away. */}
      <header className="flex h-14 shrink-0 items-center gap-1 border-b border-v3-border pl-1.5 pr-4 sm:px-3 lg:h-[52px]">
        <V3IconButton
          data-testid="template-editor-back"
          label={t("templateEditorV32.back")}
          onClick={close}
        >
          <ChevronLeft className="!size-5" />
        </V3IconButton>
        <h1 className="min-w-0 flex-1 truncate text-[15px] font-semibold">
          {title}
        </h1>
        {actions("top")}
      </header>

      {/* The document scrolls above the docked Ask bar (not under it), so
          the caret and the last section are never hidden behind the bar. */}
      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-y-auto"
        data-testid="template-editor-scroll"
      >
        <div className="mx-auto w-full max-w-[720px] px-4 pb-8 pt-2 sm:px-6 sm:pt-4">
          {/* The template's meta line with Preview and Save on its right
              (as the note view). Once it scrolls out of view, Preview and
              Save move into the top bar: one row, never two stacked bars. */}
          <div
            ref={metaLine.ref}
            className="flex min-h-12 items-center gap-1 py-1.5"
            data-testid="template-meta-strip"
          >
            <p
              className="min-w-0 flex-1 truncate text-[13px] tabular-nums text-v3-text-3"
              data-testid="template-meta"
            >
              {meta}
            </p>
            {actions("meta")}
          </div>
          <label className="sr-only" htmlFor="template-name">
            {t("templateEditorV32.name")}
          </label>
          <Grow
            className="mt-1 font-v3-display text-[26px] font-bold leading-8 tracking-[-0.02em] sm:text-[30px] sm:leading-9"
            data-testid="template-name"
            id="template-name"
            maxLength={100}
            placeholder={t("templateEditorV32.namePlaceholder")}
            value={doc.name}
            onChange={(name) => update({ name: name.replace(/\n/g, " ") })}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <OptionMenu<Exclude<TemplateStyle, null>>
              icon={doc.style === "paragraphs" ? <AlignLeft /> : <List />}
              label={t("templateEditorV32.style.label")}
              options={[
                {
                  value: "default",
                  label: t("templateEditorV32.style.default"),
                },
                {
                  value: "bullets",
                  label: t("templateEditorV32.style.bullets"),
                },
                {
                  value: "paragraphs",
                  label: t("templateEditorV32.style.paragraphs"),
                },
              ]}
              testId="template-style"
              value={doc.style ?? "default"}
              onChange={(value) =>
                update({ style: value === "default" ? null : value })
              }
            />
            <OptionMenu<Exclude<TemplateLength, null>>
              icon={<AlignLeft />}
              label={t("templateEditorV32.length.label")}
              options={[
                {
                  value: "default",
                  label: t("templateEditorV32.length.default"),
                },
                {
                  value: "concise",
                  label: t("templateEditorV32.length.concise"),
                },
                {
                  value: "detailed",
                  label: t("templateEditorV32.length.detailed"),
                },
              ]}
              testId="template-length"
              value={doc.length ?? "default"}
              onChange={(value) =>
                update({ length: value === "default" ? null : value })
              }
            />
            <V3Chip
              aria-pressed={doc.isDefault}
              data-testid="template-default"
              onClick={() => update({ isDefault: !doc.isDefault })}
            >
              <Star className={cn(doc.isDefault && "fill-current")} />
              {t("templateEditorV32.default")}
            </V3Chip>
          </div>

          {/* New template: start from the clinician's previous notes. */}
          {!templatePublicId &&
            (notesNotice ? (
              <FromNotesNotice
                identifiersRemoved={notesNotice.identifiersRemoved}
                summary={notesNotice.summary}
                onAgain={() => setNotesOpen(true)}
              />
            ) : (
              <FromNotesCard onOpen={() => setNotesOpen(true)} />
            ))}

          <div className="mt-6 rounded-[14px] bg-v3-surface-2 px-4 py-3">
            <label
              className="text-[12px] font-medium uppercase tracking-[0.04em] text-v3-text-3"
              htmlFor="template-general"
            >
              {t("templateEditorV32.general.label")}
            </label>
            <Grow
              className="mt-1 min-h-6 text-[14.5px] leading-[21px]"
              data-testid="template-general"
              id="template-general"
              placeholder={t("templateEditorV32.general.placeholder")}
              value={doc.general}
              onChange={(general) => update({ general })}
            />
          </div>

          <h2 className="mt-7 text-[12px] font-medium uppercase tracking-[0.04em] text-v3-text-3">
            {t("templateEditorV32.sections")}{" "}
            <span className="tabular-nums">· {doc.sections.length}</span>
          </h2>
          <p className="sr-only" id="template-section-move-hint">
            {t("templateEditorV32.section.moveHint")}
          </p>
          <ol className="mt-2 space-y-2.5">
            {doc.sections.map((section, index) => (
              <SectionCard
                key={section.key}
                cardRef={(element) => {
                  if (element) cards.current.set(section.key, element);
                  else cards.current.delete(section.key);
                }}
                count={doc.sections.length}
                dragging={dragKey === section.key}
                index={index}
                section={section}
                onChange={(next) => setSection(index, next)}
                onDragStart={startDrag(section.key)}
                onMove={(to) =>
                  update({ sections: move(doc.sections, index, to) })
                }
                onRemove={() =>
                  update({
                    sections: doc.sections.filter((_, i) => i !== index),
                  })
                }
              />
            ))}
          </ol>
          <button
            className="mt-2.5 flex h-12 w-full items-center justify-center gap-2 rounded-[14px] border-[1.5px] border-dashed border-v3-border-strong text-[14px] font-semibold text-v3-text-2 transition-colors hover:bg-v3-surface-2 focus-visible:outline-2 focus-visible:outline-v3-brand"
            data-testid="template-add-section"
            type="button"
            onClick={() =>
              update({ sections: [...doc.sections, emptySection()] })
            }
          >
            <Plus aria-hidden className="size-4" />
            {t("templateEditorV32.addSection")}
          </button>
          {problem && (
            <p
              className="mt-4 rounded-xl bg-v3-rec-tint px-4 py-3 text-[14px] text-v3-rec-text"
              role="alert"
            >
              {problem}
            </p>
          )}
        </div>
      </div>

      {/* Docked at the bottom on a solid background, above the safe area. */}
      <div
        className="relative z-20 shrink-0 bg-v3-panel px-3 pb-[max(12px,env(safe-area-inset-bottom))] pt-2 shadow-[0_-1px_0_var(--v3-border)] sm:px-4"
        data-testid="template-ask-dock"
      >
        <div className="mx-auto w-full max-w-[688px] space-y-2">
          {proposal.failed && (
            <p
              className="rounded-xl bg-v3-rec-tint px-4 py-2.5 text-[13.5px] text-v3-rec-text"
              role="alert"
            >
              {t("templateEditorV32.proposal.failed")}
            </p>
          )}
          {proposal.proposing && <TemplateRewriteProgress />}
          {result && (
            <ProposalCard
              result={result}
              onApply={() => {
                if (!proposal.isCurrent()) return;
                // A name typed after Send wins over the suggested one.
                const renamed =
                  doc.name.trim() && doc.name.trim() !== (asked?.name ?? "");
                update(
                  applyProposal(
                    doc,
                    result.proposal,
                    renamed
                      ? doc.name
                      : result.proposal.name.trim() || doc.name,
                  ),
                );
                proposal.clear();
                setInstruction("");
              }}
              onDiscard={() => {
                proposal.clear();
                setInstruction("");
              }}
            />
          )}
          <AskBar
            label={t("templateEditorV32.ask.label")}
            languageChip={false}
            placeholder={
              templatePublicId || write.headers.length
                ? t("templateEditorV32.ask.placeholder")
                : t("templateEditorV32.ask.placeholderNew")
            }
            proposing={proposal.proposing}
            testId="template-ask"
            value={ask}
            onChange={setAsk}
            onSend={() => {
              const text = ask.trim();
              if (!text) return;
              setAsked(
                write.name || write.headers.length || write.optionalText
                  ? {
                      name: write.name,
                      headers: write.headers,
                      rules: write.optionalText,
                    }
                  : undefined,
              );
              setInstruction(text);
              setAsk("");
              setRequest((value) => value + 1);
            }}
            onStop={() => {
              proposal.clear();
              setInstruction("");
            }}
          />
        </div>
      </div>

      {!templatePublicId && (
        <TemplateFromNotesDialog
          open={notesOpen}
          replaces={
            !!doc.general.trim() ||
            doc.sections.some(
              (section) =>
                section.heading.trim() || section.instructions.trim(),
            )
          }
          onExtracted={({ draft, ...notice }) => {
            update(applyTemplateDraft(doc, draft));
            setNotesNotice(notice);
            setNotesOpen(false);
          }}
          onOpenChange={setNotesOpen}
        />
      )}
      <V3Drawer
        open={previewOpen}
        testId="template-preview-drawer"
        title={t("templateEditorV32.preview.title")}
        width="wide"
        onOpenChange={setPreviewOpen}
      >
        <PreviewBody doc={doc} />
      </V3Drawer>
      <V3ConfirmSheet
        destructive
        cancelLabel={t("templateEditorV32.unsaved.keep")}
        confirmLabel={t("templateEditorV32.unsaved.confirm")}
        description={t("templateEditorV32.unsaved.description")}
        open={blocker.status === "blocked"}
        testId="template-unsaved"
        title={t("templateEditorV32.unsaved.title")}
        onConfirm={() => {
          setLeaving(true);
          blocker.proceed?.();
        }}
        onOpenChange={(open) => {
          if (!open && blocker.status === "blocked") blocker.reset?.();
        }}
      />
    </div>
  );
};
